import { useTranslation } from "react-i18next";
import { Fingerprint, MessagesSquare, Target } from "lucide-react";

export default function About() {
  const { t, i18n } = useTranslation();
  const isIT = i18n.language.startsWith("it");

  const sections = isIT
    ? [
        {
          h: "Il percorso iniziale",
          p: "Plan It Studio nasce nel 2022 dall'incontro professionale e istintivo tra i geometri Alessio Uliveti e Andrea Conti. Spinti da una vocazione giovanile per l'architettura — vissuta fin da bambini come il desiderio di immaginare e organizzare gli spazi interni — i due fondatori hanno condiviso fin da subito l'intento di uscire dai canoni della progettazione tradizionale, spesso confinata ai limiti della rappresentazione bidimensionale su carta.",
        },
        {
          h: "L'idea e la realizzazione dello spazio dimostrativo",
          p: "Dall'intuizione di superare i filtri e le illusioni della tecnologia virtuale, lo studio ha preso una direzione concreta allestendo uno spazio innovativo unico nel suo genere: un ambiente fisico dotato di moduli componibili a grandezza reale. Questo \u201Cteatro\u201D dinamico permette ai committenti — inclusi i clienti più indecisi — di agire direttamente, valutando distanze, proporzioni e arredi in scala 1:1, trasformando il progetto in un'esperienza interattiva e condivisa.",
        },
        {
          h: "L'obiettivo dello studio e del progetto",
          p: "L'obiettivo di Plan It Studio è restituire alla progettazione un valore non virtuale ma profondamente sostanziale. Attraverso un metodo empirico che unisce competenza tecnica, ingegno e sensibilità, lo studio punta ad azzerare le incomprensioni e a prevenire ogni criticità nascosta, portando nel quotidiano una progettazione di alto livello in cui il cliente è co-protagonista del proprio spazio.",
        },
      ]
    : [
        {
          h: "The beginning",
          p: "Plan It Studio was born in 2022 from the professional and instinctive meeting of surveyors Alessio Uliveti and Andrea Conti. Driven by a lifelong vocation for architecture — the childhood urge to imagine and organise interior spaces — the two founders shared from day one the intent to step outside traditional design, often confined to two-dimensional paper representation.",
        },
        {
          h: "The idea and the showroom",
          p: "From the insight to overcome the filters and illusions of virtual technology, the studio took a concrete direction by building an innovative space, one of a kind: a physical environment equipped with modular real-scale blocks. This dynamic \u201Ctheatre\u201D allows clients — including the most undecided — to act directly, assessing distances, proportions and furniture at 1:1 scale, turning the project into an interactive, shared experience.",
        },
        {
          h: "The goal of the studio and the project",
          p: "The goal of Plan It Studio is to give design a value that is not virtual but deeply substantial. Through an empirical method combining technical skill, ingenuity and sensitivity, the studio aims to eliminate misunderstandings and prevent hidden criticalities, bringing high-level design into everyday life where the client is a co-protagonist of their own space.",
        },
      ];

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="text-center">
        <h1 className="font-serif text-5xl lg:text-6xl text-stone-900" data-testid="about-title">
          {isIT ? "La Nostra Storia" : "Our Story"}
        </h1>
        <div className="mt-4 w-16 h-[2px] bg-[#D06D4D] mx-auto" aria-hidden="true" />
      </div>

      <div className="mt-14 space-y-10">
        {sections.map((s, i) => (
          <section key={s.h} data-testid={`about-section-${i}`}>
            <h2 className="font-serif text-2xl lg:text-3xl text-stone-900">{s.h}</h2>
            <p className="mt-4 text-stone-700 text-lg leading-relaxed">{s.p}</p>
          </section>
        ))}
      </div>

      <img
        src="/about-team.webp"
        alt={isIT ? "Fondatori Plan It Studio" : "Plan It Studio founders"}
        data-testid="about-photo"
        className="mt-16 w-full h-auto object-cover rounded-sm"
      />

      <div className="mt-24 relative left-1/2 -translate-x-1/2 w-screen max-w-[1400px] px-6 lg:px-10">
        <div className="text-center">
          <h2 className="font-serif text-4xl lg:text-5xl text-stone-900" data-testid="about-values-title">
            {isIT ? "I Nostri Valori" : "Our Values"}
          </h2>
          <div className="mt-4 w-16 h-[2px] bg-[#D06D4D] mx-auto" aria-hidden="true" />
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {(isIT
            ? [
                { t: "Identità", d: "Ogni progetto è unico e studiato sulle esigenze specifiche del cliente." },
                { t: "Coinvolgimento", d: "Comunicazione chiara e costante e partecipazione attiva del cliente in ogni fase." },
                { t: "Precisione", d: "Azzeramento degli errori di cantiere grazie a una progettazione su misura verificata concretamente nello spazio dimostrativo." },
              ]
            : [
                { t: "Identity", d: "Every project is unique and tailored to the specific needs of the client." },
                { t: "Engagement", d: "Clear and constant communication with the client, actively involved at every stage." },
                { t: "Precision", d: "Zero on-site errors thanks to tailored design verified in real space at the showroom." },
              ]
          ).map((v, i) => {
            const Icon = [Fingerprint, MessagesSquare, Target][i];
            return (
              <div
                key={v.t}
                data-testid={`about-value-${i}`}
                className="bg-white border border-stone-200 rounded-2xl p-10 lg:p-12 flex flex-col shadow-[8px_8px_24px_rgba(28,25,23,0.08)] hover:shadow-[10px_10px_32px_rgba(28,25,23,0.12)] transition-shadow"
              >
                <div className="w-14 h-14 rounded-xl bg-[#D06D4D]/10 border border-[#D06D4D]/20 flex items-center justify-center">
                  <Icon size={26} className="text-[#D06D4D]" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl mt-6 text-stone-900">{v.t}</h3>
                <p className="mt-4 text-stone-700 leading-relaxed">{v.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
